import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { apiHolder as api, apis } from 'api';
import { useAppDispatch, useAppSelector } from '@web-common/state/createStore';
import { navigate } from 'gatsby';
import { actions as mailTabActions, tabId, tabType } from '@web-common/state/reducer/mailTabReducer';
import { actions as mailActions } from '@web-common/state/reducer/mailReducer';
import PageContentLayout from '@/layouts/Main/pageContentLayout';
import './mailBox.scss';
import MailColumnEntry from './components/MailColumnEntry';
import ColumnMailBox from './components/ColumnMailBox';
import RevokeToasts from './components/RevokeToasts/RevokeToasts';
import useState2RM from './hooks/useState2ReduxMock';
import { getTreeStatesByAccount, transTag2Name } from './util';
import { FLOLDER } from './common/constant';
import useGetUniqReqWrap from '@web-common/hooks/useGetUniqReqWrap';
import { getValidStoreWidth } from '@web-common/utils/utils';
import { getMainContOffsetTopHeight } from '@web-common/utils/waimao';
import Loadable from '@loadable/component';
const CustomerMailContainer = Loadable(() => import('@web-mail/components/CustomerMail'));
const SubordinateMailContainer = Loadable(() => import('@web-mail/components/SubordinateMail'));
import ColumnMailBoxContainer from '@web-mail/components/ReadMailPageContainer/ColumnMailBoxContainer';
import MailColumnEntryContainer from '@web-mail/components/ReadMailPageContainer/MailColumnEntryContainer';
import { useState2CustomerSlice, useState2SubordinateSlice, ctSliceContext, SdSliceContext } from '@web-mail/hooks/useState2SliceRedux';
import MainReadMailWrap, { RightSider } from '@web-mail/readMailAndSider';
import useAppScale from '@web-mail/hooks/useAppScale';
import EmptyContact from '@web-mail/components/StarContact/EmptyContact';
import CustomerEmptyDetail from '@web-mail/components/CustomerMail/CustomerEmpty/detail';
import { getIn18Text } from 'api';
import useStateRef from '@web-mail/hooks/useStateRef';
import useDebounceForEvent from '@web-mail/hooks/useDebounceForEvent';
import { getCustomerSliceIdByType } from '@web-mail/utils/slice';
import ColumnCustomerMailList from '@web-mail/components/CustomerMail/ColumnCustomerMailList';
import useCreateCallbackForEvent from '@web-common/hooks/useCreateCallbackForEvent';
import { useGetProductAuth } from '@web-common/hooks/useGetProductAuth';
const systemApi = api.api.getSystemApi();
const mailApi = api.api.requireLogicalApi(apis.mailApiImpl);
const storeApi = api.api.getDataStoreApi();
const eventApi = api.api.getEventApi();
const mailConfApi = api.api.requireLogicalApi(apis.mailConfApiImpl);
const performance = api.api.requireLogicalApi(apis.performanceImpl);
const height = systemApi.isWebWmEntry() ? `calc(100vh - 36px - ${getMainContOffsetTopHeight()}px)` : '100%';
const inElectron = systemApi.isElectron();
export const STORE_MAIL_LIST_HEIGHT = 'STORE_MAIL_LIST_HEIGHT';
const ReadMailPage = props => {
    const { openInNewWindow = false, mailId = '', from = '', active } = props;
    const { productVersionInfo: { navigateVersion }, } = useGetProductAuth();
    const dispatch = useAppDispatch();
    const currentTab = useAppSelector(state => state.mailTabReducer.currentTab);
    const currentTabType = useAppSelector(state => state.mailTabReducer.currentTab.type);
    const currentTabId = useAppSelector(state => state.mailTabReducer.currentTab.id);
    const currentTabLock = useAppSelector(state => { var _a, _b, _c; return !!((_c = (_b = (_a = state.mailTabReducer.currentTab) === null || _a === void 0 ? void 0 : _a.extra) === null || _b === void 0 ? void 0 : _b.cmMidList) === null || _c === void 0 ? void 0 : _c.length); });
    const selectedCustomerTab = useAppSelector(state => state.mailReducer.selectedCustomerTab);
    const currentTabQuery = useAppSelector(state => {
        var _a, _b, _c, _d;
        return {
            query: (_b = (_a = state.mailTabReducer.currentTab) === null || _a === void 0 ? void 0 : _a.extra) === null || _b === void 0 ? void 0 : _b.query,
            accountId: (_d = (_c = state.mailTabReducer.currentTab) === null || _c === void 0 ? void 0 : _c.extra) === null || _d === void 0 ? void 0 : _d.accountId,
        };
    });
    const isReadMailTab = useAppSelector(state => { var _a; return [tabType.read].includes((_a = state.mailTabReducer.currentTab) === null || _a === void 0 ? void 0 : _a.type); });
    const isCustomerTab = useMemo(() => {
        if (process.env.BUILD_ISLINGXI && !navigateVersion) {
            return false;
        }
        else {
            return currentTabId === tabId.readCustomer || currentTabType === tabType.customer;
        }
    }, [currentTabId, currentTabType, navigateVersion]);
    const isSubordinateTab = useMemo(() => currentTabId === tabId.subordinate || currentTabType === tabType.subordinate, [currentTabId, currentTabType]);
    const isTabOpenedByCustomer = useMemo(() => {
        var _a;
        if (process.env.BUILD_ISLINGXI && !navigateVersion) {
            return false;
        }
        else {
            return ((_a = currentTab.extra) === null || _a === void 0 ? void 0 : _a.from) === tabId.readCustomer;
        }
    }, [currentTabId, navigateVersion]);
    const isTabOpenedBySubordinate = useMemo(() => { var _a; return ((_a = currentTab.extra) === null || _a === void 0 ? void 0 : _a.from) === tabId.subordinate; }, [currentTabId]);
    const sliceIdCm = useMemo(() => getCustomerSliceIdByType(selectedCustomerTab), [selectedCustomerTab]);
    const sliceIdSd = useMemo(() => {
        let id = currentTabId;
        if (currentTabType !== tabType.subordinate) {
            id = tabId.subordinate;
        }
        return id;
    }, [currentTabId, currentTabType]);
    const [mailSearching_main] = useState2RM('mailSearching');
    const [mailSearching_cm] = useState2CustomerSlice('mailSearching', undefined, sliceIdCm);
    const mailSearching = useMemo(() => {
        if (currentTabType == tabType.customer) {
            return mailSearching_cm;
        }
        if (currentTabType == tabType.subordinate) {
            return false;
        }
        return mailSearching_main;
    }, [currentTabType, mailSearching_main, mailSearching_cm]);
    const isSearching = useMemo(() => !!mailSearching, [mailSearching]);
    const [selectedSearchKeys] = useState2RM('selectedSearchKeys');
    const [searchMail, setSearchMail] = useState2RM('activeSearchMailId');
    const [selectedMail, setSelectedMail] = useState2RM('selectedMailId');
    const [selectedMailCm] = useState2CustomerSlice('selectedMailId', undefined, sliceIdCm);
    const [selectedMailSd] = useState2SubordinateSlice('selectedMailId', undefined, sliceIdSd);
    const [searchMailIdSd] = useState2SubordinateSlice('activeSearchMailId', undefined, sliceIdSd);
    const [showSubordinateMock] = useState2SubordinateSlice('showSubordinateMock');
    const [mailSearchState] = useState2SubordinateSlice('mailSearchState', undefined, sliceIdSd);
    const isSearchingSd = useMemo(() => !!mailSearchState, [mailSearchState]);
    const [, setMailListResizeProcessing] = useState2RM('mailListResizeProcessing');
    const [selectedKeys] = useState2RM('selectedKeys');
    const [mailTPDetailQuery] = useState2RM('mailTPDetailQuery');
    const [configMailLayout, setConfigMailLayout] = useState2RM('configMailLayout', 'doUpdateConfigMailLayout');
    const isLeftRight = useMemo(() => configMailLayout === '1', [configMailLayout]);
    const isUpDown = useMemo(() => configMailLayout === '3', [configMailLayout]);
    const [, setQuickSettingVisible] = useState2RM('configMailShow', 'doUpdateConfigMailShow');
    const [mailEntities] = useState2RM('mailEntities');
    const [mailTreeStateMap] = useState2RM('mailTreeStateMap', 'doUpdateMailTreeState');
    const [tagName] = useState2RM('mailTagFolderActiveKey');
    const [eventID, setEventID] = useState(null);
    const showReadMail = useMemo(() => isLeftRight || isUpDown || (!isLeftRight && currentTabType == tabType.read), [isLeftRight, currentTabType, isUpDown]);
    useMemo(() => {
        try {
            if ((selectedMail === null || selectedMail === void 0 ? void 0 : selectedMail.id) != null) {
                performance.time({
                    statKey: 'mail_readmail_switch',
                    statSubKey: '',
                    params: {
                        mailId: selectedMail === null || selectedMail === void 0 ? void 0 : selectedMail.id,
                        accountId: (selectedMail === null || selectedMail === void 0 ? void 0 : selectedMail.accountId) + '',
                    },
                });
            }
        }
        catch (e) {
            console.error(e);
        }
    }, [(selectedMail === null || selectedMail === void 0 ? void 0 : selectedMail.id) + '' + (selectedMail === null || selectedMail === void 0 ? void 0 : selectedMail.accountId)]);
    useMemo(() => {
        try {
            if ((selectedMail === null || selectedMail === void 0 ? void 0 : selectedMail.id) != null) {
                performance.time({
                    statKey: 'mail_folder_switch',
                    statSubKey: '',
                    params: {
                        folderId: selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.id,
                        accountId: (selectedMail === null || selectedMail === void 0 ? void 0 : selectedMail.accountId) + '',
                    },
                });
            }
        }
        catch (e) {
            console.error(e);
        }
    }, [(selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.id) + '' + (selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.accountId)]);
    const isMainTab = useMemo(() => {
        if (currentTabId === null || currentTabId === void 0 ? void 0 : currentTabId.includes('/')) {
            return currentTabId.split('/')[0] === tabId.readMain;
        }
        return currentTabId === tabId.readMain;
    }, [currentTabId]);
    const showMailColumn = useMemo(() => isMainTab || isCustomerTab || isSubordinateTab, [isMainTab, isCustomerTab, isSubordinateTab, isUpDown]);
    const readMailUniqWarp = useGetUniqReqWrap();
    useEffect(() => {
        var _a, _b, _c;
        if (currentTabType == tabType.read) {
            let mid = currentTabId;
            if (currentTab.type === tabType.read && (currentTabId === null || currentTabId === void 0 ? void 0 : currentTabId.includes('_'))) {
                mid = ((_a = currentTab.extra) === null || _a === void 0 ? void 0 : _a.originMid) || currentTabId.split('_')[0];
            }
            if (isSearching) {
                setSearchMail({
                    id: mid,
                    accountId: (_b = currentTab === null || currentTab === void 0 ? void 0 : currentTab.extra) === null || _b === void 0 ? void 0 : _b.accountId,
                    lock: currentTabLock,
                });
            }
            else {
                setSelectedMail({
                    id: mid,
                    accountId: (_c = currentTab === null || currentTab === void 0 ? void 0 : currentTab.extra) === null || _c === void 0 ? void 0 : _c.accountId,
                    lock: currentTabLock,
                });
            }
        }
    }, [currentTabId, currentTabLock]);
    const setTab = (isActive = false) => {
        var _a, _b;
        if (!isMainTab) {
            return;
        }
        let title;
        if (isSearching) {
            title = getIn18Text('SOUSUOYOUJIAN');
        }
        else {
            if (selectedKeys.id == FLOLDER.TAG) {
                title = transTag2Name(tagName === null || tagName === void 0 ? void 0 : tagName.key);
            }
            else {
                const curTreeMap = (_a = getTreeStatesByAccount(mailTreeStateMap, selectedKeys.accountId || '')) === null || _a === void 0 ? void 0 : _a.MailFolderTreeMap;
                title = (curTreeMap && ((_b = curTreeMap[selectedKeys.id]) === null || _b === void 0 ? void 0 : _b.entry.mailBoxName)) || getIn18Text('SHOUJIANXIANG');
            }
        }
        if (!title) {
            title = '_';
        }
        const mailTabModel = {
            id: '-1',
            title,
            type: tabType.readMain,
            closeable: false,
            isActive,
        };
        dispatch(mailTabActions.doChangeTabById({
            id: '-1',
            tabModel: mailTabModel,
        }));
    };
    useEffect(() => {
        setTab();
    }, [isSearching, `${tagName === null || tagName === void 0 ? void 0 : tagName.key}${tagName === null || tagName === void 0 ? void 0 : tagName.accountId}`]);
    useEffect(() => {
        setTab();
    }, [mailTreeStateMap]);
    useEffect(() => {
        if (isSearching) {
            if (currentTabType === tabType.readMain || (currentTabType === tabType.read && !isTabOpenedByCustomer && !isTabOpenedBySubordinate)) {
                dispatch(mailTabActions.doChangeCurrentTab('-1/-1'));
            }
        }
    }, [selectedSearchKeys]);
    useEffect(() => {
        if (active) {
            const val = mailConfApi.getMailPageLayout();
            setConfigMailLayout(val);
            setQuickSettingVisible(false);
        }
    }, [active]);
    useEffect(() => {
        if (isLeftRight && currentTabType === tabType.read) {
            dispatch(mailTabActions.doChangeCurrentTab('-1/-1'));
        }
    }, [isLeftRight]);
    const registeSendingMail = async () => {
        if (inElectron) {
            const curWinInfo = await systemApi.getCurrentWinInfo();
            if (curWinInfo.type === 'main') {
                const eventID = eventApi.registerSysEventObserver('sendingMail', {
                    func: event => {
                        if (event && event.eventData) {
                            navigate('/#mailbox');
                            dispatch(mailTabActions.doChangeCurrentTab('-1'));
                            dispatch(mailActions.doAddSendingMails(event.eventData));
                        }
                    },
                });
                setEventID(eventID);
            }
        }
    };
    useEffect(() => {
        registeSendingMail();
        return () => {
            eventID && eventApi.unregisterSysEventObserver('sendingMail', eventID);
        };
    }, []);
    const appColumnSize = useAppScale();
    const folderListWidthRef = useStateRef(appColumnSize.folderListWidth);
    const folderListMaxConstraintsRef = useStateRef(appColumnSize.folderListMaxConstraints);
    const mailListWidthRef = useStateRef(appColumnSize.mailListWidth);
    const mailListMaxConstraintsRef = useStateRef(appColumnSize.mailListMaxConstraints);
    const columnMailBox = useMemo(() => {
        return (React.createElement(ColumnMailBoxContainer, { isLeftRight: isLeftRight, height: height, isMainTab: isMainTab, defaultWidth: folderListWidthRef.current, openInNewWindow: openInNewWindow, maxWidth: folderListMaxConstraintsRef.current, setMailListResizeProcessing: setMailListResizeProcessing, SideContentLayout: true },
            React.createElement(ColumnMailBox, null)));
    }, [isLeftRight, isMainTab, openInNewWindow, appColumnSize.folderListMaxConstraints, folderListWidthRef.current]);
    const [updateUpdwonConfig, setUpdwonConfig] = useState(0);
    const handleResize = useDebounceForEvent(event => {
        if (isUpDown) {
            setUpdwonConfig(num => num + 1);
        }
    }, 500, { leading: false, trailing: true });
    useEffect(() => {
        if (isUpDown) {
            window.addEventListener('resize', handleResize);
        }
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isUpDown]);
    const mailColumnEntry = useMemo(() => {
        let outerProps = {};
        if ((isLeftRight || isUpDown) && showMailColumn) {
            let upDownProps = {
                defaultWidth: mailListWidthRef.current || appColumnSize.mailListWidth,
                defaultheight: '100%',
            };
            if (isUpDown) {
                let storeListWidth = getValidStoreWidth(storeApi.getSync(STORE_MAIL_LIST_HEIGHT, { prefix: 'layout_storage_cache', storeMethod: 'localStorage' }));
                const overHeight = document.body.clientHeight - 35;
                const height = overHeight / 2;
                upDownProps = {
                    axis: 'y',
                    height: storeListWidth || height,
                    defaultWidth: '100%',
                    defaultheight: storeListWidth || height,
                    minConstraints: [Infinity, 192],
                    maxConstraints: [Infinity, overHeight - 176],
                    maxWidth: Infinity,
                };
            }
            outerProps = upDownProps;
        }
        return (React.createElement(MailColumnEntryContainer, { height: height, showMailColumn: showMailColumn, isLeftRight: isLeftRight, isUpDown: isUpDown, upDownProps: outerProps, defaultListWidth: mailListWidthRef.current, openInNewWindow: openInNewWindow, maxWidth: mailListMaxConstraintsRef.current, setMailListResizeProcessing: setMailListResizeProcessing, SideContentLayout: true }, isCustomerTab || isTabOpenedByCustomer ? (React.createElement(ColumnCustomerMailList, { isLeftRight: isLeftRight, isUpDown: isUpDown })) : (React.createElement(MailColumnEntry, { isLeftRight: isLeftRight, style: isLeftRight && showMailColumn ? undefined : { position: 'relative' } }))));
    }, [isLeftRight, isUpDown, showMailColumn, openInNewWindow, updateUpdwonConfig, mailListWidthRef.current, isCustomerTab, isTabOpenedByCustomer]);
    const CurMailContent = useMemo(() => {
        if (currentTabType == tabType.customer) {
            return mailEntities[selectedMailCm === null || selectedMailCm === void 0 ? void 0 : selectedMailCm.id];
        }
        if (currentTabType == tabType.subordinate) {
            return mailEntities[isSearchingSd ? searchMailIdSd : selectedMailSd === null || selectedMailSd === void 0 ? void 0 : selectedMailSd.id];
        }
        return mailEntities[openInNewWindow ? mailId : isSearching ? searchMail.id : selectedMail.id];
    }, [
        mailEntities,
        openInNewWindow,
        mailId,
        isSearching,
        searchMail.id,
        selectedMail.id,
        currentTabType,
        selectedMailCm === null || selectedMailCm === void 0 ? void 0 : selectedMailCm.id,
        selectedMailSd === null || selectedMailSd === void 0 ? void 0 : selectedMailSd.id,
        searchMailIdSd,
        isSearchingSd,
    ]);
    const currentMailfrom = useMemo(() => {
        var _a, _b;
        if ((_a = currentTabQuery === null || currentTabQuery === void 0 ? void 0 : currentTabQuery.query) === null || _a === void 0 ? void 0 : _a.mid) {
            if (((_b = currentTabQuery === null || currentTabQuery === void 0 ? void 0 : currentTabQuery.query) === null || _b === void 0 ? void 0 : _b.mailFrom) === 'subordinate') {
                return 'subordinate';
            }
            else {
                return 'customer';
            }
        }
        if (currentTabType == tabType.customer) {
            return selectedMailCm === null || selectedMailCm === void 0 ? void 0 : selectedMailCm.from;
        }
        if (currentTabType == tabType.subordinate) {
            return selectedMailSd === null || selectedMailSd === void 0 ? void 0 : selectedMailSd.from;
        }
        return openInNewWindow ? from : isSearching ? searchMail === null || searchMail === void 0 ? void 0 : searchMail.from : selectedMail.from;
    }, [openInNewWindow, isSearching, searchMail.id, selectedMail.id, currentTabType, selectedMailCm === null || selectedMailCm === void 0 ? void 0 : selectedMailCm.id, selectedMailSd === null || selectedMailSd === void 0 ? void 0 : selectedMailSd.id, currentTabQuery === null || currentTabQuery === void 0 ? void 0 : currentTabQuery.query]);
    const doGetTpMailContent = useCallback((id, _noFlagInfo, noCache) => {
        var _a, _b, _c;
        const param = {
            mid: id,
            owner: CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.owner,
            subEmail: ((_a = CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.tpMailInfo) === null || _a === void 0 ? void 0 : _a.belongEmail) || (CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.owner),
            colleagueAccId: (_b = CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.tpMailInfo) === null || _b === void 0 ? void 0 : _b.colleagueAccId,
            mailFrom: CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.mailFrom,
            mainAccount: CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.mainAccount,
        };
        if ((CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.mailFrom) === 'subordinate' && ((_c = CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.tpMailInfo) === null || _c === void 0 ? void 0 : _c.subordinateFromAI)) {
            param.subordinateFromAI = true;
        }
        return mailApi.doGetTpMailContent(param, noCache);
    }, [CurMailContent]);
    const doGetTpMailContentCM = useCreateCallbackForEvent((id, _noFlagInfo, noCache) => {
        if (isReadMailTab) {
            const query = (currentTabQuery === null || currentTabQuery === void 0 ? void 0 : currentTabQuery.query) || {};
            return mailApi.doGetTpMailContent({
                mid: query === null || query === void 0 ? void 0 : query.mid,
                owner: query === null || query === void 0 ? void 0 : query.owner,
                mailFrom: 'relatedEmail',
                mainAccount: query === null || query === void 0 ? void 0 : query.mainAccount,
                type: query === null || query === void 0 ? void 0 : query.type,
                refId: query === null || query === void 0 ? void 0 : query.refId,
            }, noCache);
        }
        else {
            return mailApi.doGetTpMailContent({
                mid: mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.mid,
                owner: mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.owner,
                mailFrom: 'relatedEmail',
                mainAccount: mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.mainAccount,
                type: mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.type,
                refId: mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.refId,
            }, noCache);
        }
    });
    const doGetTpMailContentSD = useCallback((id, _noFlagInfo, noCache) => {
        if (isReadMailTab) {
            const query = (currentTabQuery === null || currentTabQuery === void 0 ? void 0 : currentTabQuery.query) || {};
            return mailApi.doGetTpMailContent({
                mid: query === null || query === void 0 ? void 0 : query.mid,
                owner: query === null || query === void 0 ? void 0 : query.owner,
                subEmail: query === null || query === void 0 ? void 0 : query.owner,
                mailFrom: 'subordinate',
                colleagueAccId: query === null || query === void 0 ? void 0 : query.colleagueAccId,
                subordinateFromAI: true,
            }, noCache);
        }
        else {
            return mailApi.doGetTpMailContent({
                mid: mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.mid,
                owner: mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.owner,
                subEmail: mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.owner,
                mailFrom: 'subordinate',
                colleagueAccId: mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.colleagueAccId,
                subordinateFromAI: true,
            }, noCache);
        }
    }, [currentTabQuery === null || currentTabQuery === void 0 ? void 0 : currentTabQuery.query, mailTPDetailQuery]);
    const [currentMailId, lock] = useMemo(() => {
        if (openInNewWindow) {
            return [mailId];
        }
        if (currentTabType == tabType.customer) {
            return [(selectedMailCm === null || selectedMailCm === void 0 ? void 0 : selectedMailCm.id) || '', selectedMailCm === null || selectedMailCm === void 0 ? void 0 : selectedMailCm.lock];
        }
        if (currentTabType == tabType.subordinate) {
            const id = isSearchingSd ? searchMailIdSd : selectedMailSd === null || selectedMailSd === void 0 ? void 0 : selectedMailSd.id;
            return [id || ''];
        }
        if (isSearching) {
            return [(searchMail === null || searchMail === void 0 ? void 0 : searchMail.id) || '', searchMail === null || searchMail === void 0 ? void 0 : searchMail.lock];
        }
        return [selectedMail.id || '', selectedMail === null || selectedMail === void 0 ? void 0 : selectedMail.lock];
    }, [
        openInNewWindow,
        mailId,
        searchMail === null || searchMail === void 0 ? void 0 : searchMail.id,
        selectedMail.id,
        sliceIdCm,
        sliceIdSd,
        currentTabType,
        selectedMailCm === null || selectedMailCm === void 0 ? void 0 : selectedMailCm.id,
        selectedMailSd === null || selectedMailSd === void 0 ? void 0 : selectedMailSd.id,
        isSearching,
        isSearchingSd,
        searchMailIdSd,
    ]);
    const emptyRender = useCallback(() => {
        if (currentTabType == tabType.customer) {
            return React.createElement(CustomerEmptyDetail, null);
        }
        if ((selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.id) == FLOLDER.STAR) {
            return React.createElement(EmptyContact, { showLogo: !isUpDown });
        }
        return null;
    }, [selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.id, isUpDown, currentTabType]);
    const readMail = useMemo(() => {
        let getSignMailContent;
        if (CurMailContent && ((CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.isTpMail) || (isCustomerTab && CurMailContent.mailFrom === 'customer'))) {
            getSignMailContent = doGetTpMailContent;
        }
        if (currentMailfrom == 'customer') {
            if (!!(mailTPDetailQuery === null || mailTPDetailQuery === void 0 ? void 0 : mailTPDetailQuery.isSelf)) {
                getSignMailContent = null;
            }
            else {
                getSignMailContent = doGetTpMailContentCM;
            }
        }
        else if (currentMailfrom == 'subordinate') {
            getSignMailContent = doGetTpMailContentSD;
        }
        return (React.createElement(MainReadMailWrap, { SideContentLayout: true, showReadMail: showReadMail, id: currentMailId, accountId: (currentTabQuery === null || currentTabQuery === void 0 ? void 0 : currentTabQuery.accountId) || '', height: isUpDown ? '100%' : height, isUpDown: isUpDown, from: from, openInNewWindow: openInNewWindow, tempContent: CurMailContent, requestUniqWrap: readMailUniqWarp, readOnly: CurMailContent && (CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.isTpMail), getSignMailContent: getSignMailContent, emptyRender: emptyRender }));
    }, [showReadMail, openInNewWindow, currentMailId, doGetTpMailContent, doGetTpMailContentSD, CurMailContent === null || CurMailContent === void 0 ? void 0 : CurMailContent.id, emptyRender, isCustomerTab, currentMailfrom]);
    const rightSiderMemo = useMemo(() => React.createElement(RightSider, { "data-sidecontent": true, id: currentMailId, lock: lock }), [currentMailId, lock]);
    const [style] = useState({ display: 'flex', flex: 1, overflow: 'hidden', height: '100%' });
    const listAndContent = useMemo(() => {
        return isUpDown ? (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "listAndContent", "data-sidecontent": true },
                !openInNewWindow && mailColumnEntry,
                readMail),
            rightSiderMemo)) : (React.createElement(React.Fragment, null,
            mailColumnEntry,
            readMail,
            rightSiderMemo));
    }, [isUpDown, openInNewWindow, mailColumnEntry, readMail, rightSiderMemo]);
    if (isCustomerTab || isTabOpenedByCustomer) {
        return (React.createElement(ctSliceContext.Provider, { value: sliceIdCm },
            React.createElement(CustomerMailContainer, { style: style, isLeftRight: isLeftRight, isUpDown: isUpDown, openInNewWindow: openInNewWindow, defaultWidth: appColumnSize.folderListWidth, height: height, showMailColumn: showMailColumn, defaultListWidth: appColumnSize.mailListWidth, setMailListResizeProcessing: setMailListResizeProcessing, folderMaxWidth: appColumnSize.folderListMaxConstraints, listMaxWidth: appColumnSize.mailListMaxConstraints, listAndContentDom: listAndContent })));
    }
    if (isSubordinateTab || isTabOpenedBySubordinate) {
        return (React.createElement(SdSliceContext.Provider, { value: sliceIdSd },
            React.createElement(SubordinateMailContainer, { style: style, isLeftRight: isLeftRight, isUpDown: isUpDown, updateUpdwonConfig: updateUpdwonConfig, openInNewWindow: openInNewWindow, defaultWidth: appColumnSize.folderListWidth, height: height, showMailColumn: showMailColumn, defaultListWidth: appColumnSize.mailListWidth, setMailListResizeProcessing: setMailListResizeProcessing, folderMaxWidth: appColumnSize.folderListMaxConstraints, listMaxWidth: appColumnSize.mailListMaxConstraints, showSubordinateMock: !!showSubordinateMock }, readMail)));
    }
    return (React.createElement(React.Fragment, null,
        !openInNewWindow && React.createElement(RevokeToasts, null),
        React.createElement(PageContentLayout, { style: style },
            columnMailBox,
            listAndContent)));
};
export default ReadMailPage;
